"use client";

import React, { useMemo } from 'react';
import { useUsage, usePricing } from '../hooks/usePricing';

/**
 * Props for usage display components
 */
interface UsageDisplayProps {
  feature: string;
  showLabel?: boolean;
  className?: string;
}

/**
 * Simple component to display current usage for a feature
 */
export function UsageDisplay({
  feature,
  showLabel = true,
  className = ''
}: UsageDisplayProps) {
  const { getUsage } = useUsage();
  const usage = getUsage(feature);

  return (
    <div className={`usage-display ${className}`}>
      {showLabel && (
        <span className="usage-label">
          {feature.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
        </span>
      )}
      <span className="usage-value">
        {usage !== null ? usage : 0}
      </span>
    </div>
  );
}

/**
 * Component to display usage for multiple features
 */
interface MultiUsageDisplayProps {
  features: string[];
  title?: string;
  className?: string;
}

export function MultiUsageDisplay({
  features,
  title = 'Usage Overview',
  className = ''
}: MultiUsageDisplayProps) {
  const { getAllUsage } = useUsage();
  const allUsage = getAllUsage();

  const relevantUsage = useMemo(() => {
    return features.reduce((acc, feature) => {
      acc[feature] = allUsage[feature] || 0;
      return acc;
    }, {} as Record<string, number>);
  }, [features, allUsage]);

  return (
    <div className={`multi-usage-display ${className}`}>
      {title && <h3 className="usage-title">{title}</h3>}
      <div className="usage-grid">
        {Object.entries(relevantUsage).map(([feature, usage]) => (
          <div key={feature} className="usage-item">
            <div className="usage-feature-name">
              {feature.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </div>
            <div className="usage-feature-value">{usage}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Component to track and display usage in real-time
 */
interface UsageTrackerProps extends UsageDisplayProps {
  onUsageChange?: (usage: number) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export function UsageTracker({
  feature,
  showLabel = true,
  onUsageChange,
  autoRefresh = false,
  refreshInterval = 30000,
  className = ''
}: UsageTrackerProps) {
  const { getUsage } = useUsage();
  const { refreshFeatures } = usePricing();
  const usage = getUsage(feature);

  // Auto-refresh usage data
  React.useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refreshFeatures();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refreshFeatures]);

  // Notify of usage changes
  React.useEffect(() => {
    if (usage !== null && onUsageChange) {
      onUsageChange(usage);
    }
  }, [usage, onUsageChange]);

  return (
    <div className={`usage-tracker ${className}`}>
      <UsageDisplay feature={feature} showLabel={showLabel} />
      {autoRefresh && (
        <div className="usage-refresh-indicator" style={{ fontSize: '0.75rem', color: '#666' }}>
          Auto-refreshing every {Math.floor(refreshInterval / 1000)}s
        </div>
      )}
    </div>
  );
}

/**
 * Component for tracking usage with manual increment buttons
 */
interface UsageControlProps extends UsageDisplayProps {
  incrementValue?: number;
  showControls?: boolean;
  onTrack?: (feature: string, quantity: number) => void;
}

export function UsageControl({
  feature,
  showLabel = true,
  incrementValue = 1,
  showControls = true,
  onTrack,
  className = ''
}: UsageControlProps) {
  const { trackUsage } = useUsage();
  const [isTracking, setIsTracking] = React.useState(false);

  const handleTrack = async () => {
    setIsTracking(true);
    try {
      await trackUsage(feature, incrementValue);
      onTrack?.(feature, incrementValue);
    } catch (error) {
      console.error('Failed to track usage:', error);
    } finally {
      setIsTracking(false);
    }
  };

  return (
    <div className={`usage-control ${className}`}>
      <div className="usage-display-section">
        <UsageDisplay feature={feature} showLabel={showLabel} />
      </div>

      {showControls && (
        <div className="usage-controls">
          <button
            onClick={handleTrack}
            disabled={isTracking}
            className="usage-increment-btn"
            style={{
              padding: '4px 8px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isTracking ? 'not-allowed' : 'pointer',
              opacity: isTracking ? 0.6 : 1
            }}
          >
            {isTracking ? 'Tracking...' : `+${incrementValue}`}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Hook for usage statistics across multiple features
 */
export function useUsageStats(features: string[]) {
  const { getAllUsage } = useUsage();
  const allUsage = getAllUsage();

  return useMemo(() => {
    const relevantUsage = features.reduce((acc, feature) => {
      acc[feature] = allUsage[feature] || 0;
      return acc;
    }, {} as Record<string, number>);

    const total = Object.values(relevantUsage).reduce((sum, usage) => sum + usage, 0);
    const average = features.length > 0 ? total / features.length : 0;
    const max = Math.max(...Object.values(relevantUsage), 0);
    const min = Math.min(...Object.values(relevantUsage), 0);

    return {
      usage: relevantUsage,
      total,
      average: Math.round(average * 100) / 100,
      max,
      min,
      features: features.length
    };
  }, [features, allUsage]);
}